import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Bell,
  KeyRound,
  AtSign,
  Trash2,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import {
  enablePush,
  isPushSupported,
  getNotificationState,
} from "@/lib/push";

type View = "menu" | "notifications" | "password" | "username" | "delete";

interface SettingsPanelProps {
  onClose: () => void;
  onAccountDeleted: () => void;
}

export function SettingsPanel({ onClose, onAccountDeleted }: SettingsPanelProps) {
  const [view, setView] = useState<View>("menu");

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Header */}
      <div className="px-5 pt-2 pb-4 flex items-center gap-3 flex-shrink-0">
        {view === "menu" ? (
          <h2 className="text-white font-display font-bold text-xl" data-testid="text-settings-title">
            Settings
          </h2>
        ) : (
          <>
            <button
              data-testid="button-settings-back"
              onClick={() => setView("menu")}
              className="text-zinc-400 hover:text-white transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-white font-display font-bold text-xl">
              {view === "notifications" && "Notifications"}
              {view === "password" && "Change password"}
              {view === "username" && "Change username"}
              {view === "delete" && "Delete account"}
            </h2>
          </>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-6">
        {view === "menu" && (
          <div className="space-y-2">
            <SettingsRow
              testId="button-settings-notifications"
              icon={<Bell className="w-5 h-5 text-blue-400 shrink-0" />}
              title="Notifications"
              subtitle="Choose what you get alerted about"
              onClick={() => setView("notifications")}
            />
            <SettingsRow
              testId="button-settings-password"
              icon={<KeyRound className="w-5 h-5 text-amber-400 shrink-0" />}
              title="Change password"
              subtitle="Update your account password"
              onClick={() => setView("password")}
            />
            <SettingsRow
              testId="button-settings-username"
              icon={<AtSign className="w-5 h-5 text-emerald-400 shrink-0" />}
              title="Change username"
              subtitle="Pick a new @username"
              onClick={() => setView("username")}
            />
            <SettingsRow
              testId="button-settings-delete"
              icon={<Trash2 className="w-5 h-5 text-red-400 shrink-0" />}
              title="Delete account"
              subtitle="Permanently remove your account"
              onClick={() => setView("delete")}
            />
          </div>
        )}

        {view === "notifications" && <NotificationsSettings />}
        {view === "password" && <ChangePasswordForm onDone={() => setView("menu")} />}
        {view === "username" && <ChangeUsernameForm onDone={() => setView("menu")} />}
        {view === "delete" && (
          <DeleteAccountForm onDeleted={onAccountDeleted} />
        )}
      </div>
    </div>
  );
}

function SettingsRow({
  testId,
  icon,
  title,
  subtitle,
  onClick,
}: {
  testId: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/50 transition-all text-left"
    >
      {icon}
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm">{title}</p>
        <p className="text-zinc-500 text-xs">{subtitle}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
    </button>
  );
}

interface NotificationPrefs {
  notifyFriendRequests: boolean;
  notifyFriendVibes: boolean;
  notifyVenueAlerts: boolean;
  notifyMediaReactions: boolean;
  pushEnabled: boolean;
}

function NotificationsSettings() {
  const { toast } = useToast();
  const [busyPush, setBusyPush] = useState(false);
  const { data: prefs, isLoading } = useQuery<NotificationPrefs>({
    queryKey: ["/api/user/notification-prefs"],
  });

  const updatePref = useMutation({
    mutationFn: (patch: Partial<NotificationPrefs>) =>
      apiRequest("PATCH", "/api/user/notification-prefs", patch),
    onMutate: async (patch) => {
      const prev = queryClient.getQueryData<NotificationPrefs>(["/api/user/notification-prefs"]);
      if (prev) {
        queryClient.setQueryData(["/api/user/notification-prefs"], { ...prev, ...patch });
      }
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["/api/user/notification-prefs"], ctx.prev);
      toast({ title: "Couldn't update setting", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/notification-prefs"] });
    },
  });

  const handlePushToggle = async (next: boolean) => {
    setBusyPush(true);
    try {
      if (next) {
        if (!isPushSupported()) {
          toast({ title: "Push isn't supported on this device", variant: "destructive" });
          return;
        }
        const result = await enablePush();
        if (result === "granted") {
          toast({ title: "Push notifications on" });
        } else if (result === "denied") {
          toast({
            title: "Notifications are blocked",
            description: "Enable them in your browser settings, then try again.",
            variant: "destructive",
          });
        }
      } else {
        await apiRequest("DELETE", "/api/user/push-subscription");
        toast({ title: "Push notifications off" });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/user/notification-prefs"] });
    } catch {
      toast({ title: "Couldn't change push notifications", variant: "destructive" });
    } finally {
      setBusyPush(false);
    }
  };

  if (isLoading || !prefs) {
    return (
      <div className="space-y-2" data-testid="status-prefs-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-2xl bg-zinc-900 animate-pulse" />
        ))}
      </div>
    );
  }

  const pushBlocked = getNotificationState() === "denied";

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <ToggleRow
          testId="toggle-push"
          title="Push notifications"
          subtitle={pushBlocked ? "Blocked in browser settings" : "Get alerts on this device"}
          checked={prefs.pushEnabled}
          disabled={busyPush}
          onCheckedChange={handlePushToggle}
        />
      </div>

      <div className="space-y-2">
        <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider px-1">Categories</p>
        <ToggleRow
          testId="toggle-friend-requests"
          title="Friend requests"
          subtitle="When someone adds you or accepts your request"
          checked={prefs.notifyFriendRequests}
          disabled={updatePref.isPending}
          onCheckedChange={(v) => updatePref.mutate({ notifyFriendRequests: v })}
        />
        <ToggleRow
          testId="toggle-friend-vibes"
          title="Friend vibes"
          subtitle="When friends report a vibe near you"
          checked={prefs.notifyFriendVibes}
          disabled={updatePref.isPending}
          onCheckedChange={(v) => updatePref.mutate({ notifyFriendVibes: v })}
        />
        <ToggleRow
          testId="toggle-venue-alerts"
          title="Venue alerts"
          subtitle="When a venue gets busy or quiet"
          checked={prefs.notifyVenueAlerts}
          disabled={updatePref.isPending}
          onCheckedChange={(v) => updatePref.mutate({ notifyVenueAlerts: v })}
        />
        <ToggleRow
          testId="toggle-media-reactions"
          title="Reactions to your posts"
          subtitle="When people react to your photos and videos"
          checked={prefs.notifyMediaReactions}
          disabled={updatePref.isPending}
          onCheckedChange={(v) => updatePref.mutate({ notifyMediaReactions: v })}
        />
      </div>
    </div>
  );
}

function ToggleRow({
  testId,
  title,
  subtitle,
  checked,
  disabled,
  onCheckedChange,
}: {
  testId: string;
  title: string;
  subtitle: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-900 border border-zinc-800">
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium text-sm">{title}</p>
        <p className="text-zinc-500 text-xs">{subtitle}</p>
      </div>
      <Switch
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/auth/change-password", {
        currentPassword: currentPassword || undefined,
        newPassword,
      }),
    onSuccess: () => {
      toast({ title: "Password updated" });
      onDone();
    },
    onError: (err: any) => {
      toast({ title: parseError(err, "Failed to change password"), variant: "destructive" });
    },
  });

  const canSubmit =
    newPassword.length >= 8 && newPassword === confirm && !mutation.isPending;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">Current password</Label>
        <Input
          data-testid="input-current-password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Leave blank if you signed up with Google/email link"
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-xl"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">New password</Label>
        <Input
          data-testid="input-new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="At least 8 characters"
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-xl"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">Confirm new password</Label>
        <Input
          data-testid="input-confirm-password"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-xl"
        />
      </div>
      {confirm.length > 0 && newPassword !== confirm && (
        <p className="text-red-400 text-xs" data-testid="text-password-mismatch">Passwords don't match</p>
      )}
      <Button
        data-testid="button-save-password"
        onClick={() => mutation.mutate()}
        disabled={!canSubmit}
        className="w-full rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        {mutation.isPending ? "Saving..." : "Save password"}
      </Button>
    </div>
  );
}

function ChangeUsernameForm({ onDone }: { onDone: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [username, setUsername] = useState(user?.username ?? "");

  const mutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/auth/change-username", { username: username.trim() }),
    onSuccess: () => {
      toast({ title: "Username updated" });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      onDone();
    },
    onError: (err: any) => {
      toast({ title: parseError(err, "Failed to change username"), variant: "destructive" });
    },
  });

  const trimmed = username.trim();
  const valid = /^[a-zA-Z0-9_]{3,20}$/.test(trimmed);
  const changed = trimmed.toLowerCase() !== (user?.username ?? "").toLowerCase();

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">Username</Label>
        <div className="flex items-center gap-2 rounded-xl bg-zinc-900 border border-zinc-800 px-3">
          <span className="text-zinc-500">@</span>
          <Input
            data-testid="input-new-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="bg-transparent border-0 text-white placeholder:text-zinc-600 px-0 focus-visible:ring-0"
          />
        </div>
        <p className="text-zinc-500 text-xs">3-20 characters, letters, numbers and underscores only.</p>
      </div>
      <Button
        data-testid="button-save-username"
        onClick={() => mutation.mutate()}
        disabled={!valid || !changed || mutation.isPending}
        className="w-full rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        {mutation.isPending ? "Saving..." : "Save username"}
      </Button>
    </div>
  );
}

function DeleteAccountForm({ onDeleted }: { onDeleted: () => void }) {
  const { toast } = useToast();
  const [confirmText, setConfirmText] = useState("");

  const mutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/auth/delete-account"),
    onSuccess: () => {
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      toast({ title: "Account deleted" });
      onDeleted();
    },
    onError: (err: any) => {
      toast({ title: parseError(err, "Failed to delete account"), variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4">
        <p className="text-white font-medium text-sm mb-1">This can't be undone</p>
        <p className="text-zinc-400 text-xs">
          Deleting your account permanently removes your profile, vibes, photos, friends, Honey
          balance and all related data.
        </p>
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">Type DELETE to confirm</Label>
        <Input
          data-testid="input-delete-confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-xl"
        />
      </div>
      <Button
        data-testid="button-confirm-delete-account"
        onClick={() => mutation.mutate()}
        disabled={confirmText !== "DELETE" || mutation.isPending}
        className="w-full rounded-xl bg-red-600 hover:bg-red-700 text-white"
      >
        {mutation.isPending ? "Deleting..." : "Delete my account"}
      </Button>
    </div>
  );
}

function parseError(err: any, fallback: string): string {
  try {
    const text = err?.message || "";
    const jsonPart = text.includes("{") ? text.substring(text.indexOf("{")) : "";
    if (jsonPart) {
      const parsed = JSON.parse(jsonPart);
      return parsed.message || parsed.error || fallback;
    }
  } catch {}
  return fallback;
}
